/*
 *  Copyright (c) Qwoach LLC 2023. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 16/12/2023
 */
import BaseObject from "@shared/BaseObject.js";
import {LogicalSymbols} from "@/core/js/backend/filters/LogicalSymbols.js";

class TreeNode {
	type: string = null
	operands = []

	constructor(type: string, operands = []) {
		this.type = type;
		this.operands = operands;
	}
}
export default class NormalFormManager extends BaseObject {


	constructor() {
		super();
	}

	convertToDNF(array, transformConjunctionsCallback = (conjunctioins) => conjunctioins){

		// we need to ensure the expression is enclosed in a top level
		// AND operation, so it can be represented as a tree with a single root.
		// This eliminates cases like "(a, b), c"
		array = [LogicalSymbols.AND_START, ...array, LogicalSymbols.AND_END]

		let tree = this.arrayToTreeNodes(array)[0]
		console.log("NormalForm.convertToDNF: tree = ", tree, ", array = ", array)
		let dnf = this._convertToDNFInternal(tree)
		console.log("NormalForm.convertToDNF: dnf = ", dnf)
		let finalDnf = this.convertDnfToFinalArray(dnf, transformConjunctionsCallback)
		console.log("NormalForm.convertToDNF: finalDnf = ", finalDnf)
		return finalDnf
	}

	convertDnfToFinalArray(dnfArray, transformConjunctionsCallback){
		let array = []
		array.push(LogicalSymbols.OR_START)
		for(let i = 0; i < dnfArray.length; i++) {
			let conjunctions = transformConjunctionsCallback(dnfArray[i])
			if(conjunctions.length > 1) {
				array.push(LogicalSymbols.AND_START)
			}
			array.push(...conjunctions)
			if(conjunctions.length > 1) {
				array.push(LogicalSymbols.AND_END)
			}
		}
		array.push(LogicalSymbols.OR_END)
		return array
	}

	// _convertToCNFInternal(phi) {
	// 	if (this.isVariable(phi)) {
	// 		return [phi];
	// 	} else if (this.isDisjunction(phi)) {
	// 		let operands = phi.operands.map(operand => this._convertToCNFInternal(operand)).reduce((res, node) => res.concat(...node.operands), [])
	// 		return new TreeNode(LogicalSymbols.TYPE_OR, operands)
	// 	} else if (this.isConjunction(phi)) {
	// 		let operands = phi.operands.map(operand => this._convertToCNFInternal(operand)).reduce((res, node) => res.concat(...node.nodes), [])
	// 		return this.distributeOrOverAnd(operands);
	// 	}
	// }

	_convertToDNFInternal(phi, level = 0) {
		console.log("NormalForm.convertToDNFInternal: phi = ", phi, ", level = ", level)
		if (this.isVariable(phi)) {
			console.log("NormalForm.convertToDNFInternal: phi is variable")
			// Base case: φ is a variable
			let res = [phi];
			console.log("NormalForm.convertToDNFInternal: res = ", res, ", level = ", level)
			return res
		} else if (phi.type === LogicalSymbols.TYPE_OR) {
			// Case: φ has the form P v Q
			const disjuncts = phi.operands.map(operand => this._convertToDNFInternal(operand, level + 1));
			// console.log("NormalForm.convertToDNFInternal: disjuncts = ", disjuncts)
			console.log("NormalForm.convertToDNFInternal: phi is disjunction")
			console.log("NormalForm.convertToDNFInternal: res = ", disjuncts, ", level = ", level)
			return this.flatten(disjuncts);
		} else if (phi.type === LogicalSymbols.TYPE_AND) {
			// Case: φ has the form P ^ Q
			const conjuncts = phi.operands.map(operand => this._convertToDNFInternal(operand, level + 1));
			// console.log("NormalForm.convertToDNFInternal: conjuncts = ", conjuncts)
			let res = this.distributeAndOverOr(conjuncts);
			// if(Array.isArray(res[0])){
			// 	res = this.flatten(res)
			// }
			console.log("NormalForm.convertToDNFInternal: phi is conjunction")
			console.log("NormalForm.convertToDNFInternal: res = ", res, ", level = ", level)
			return res
		}
	}

	isVariable(phi) {
		// Check if phi is a variable (literal)
		return !(phi instanceof TreeNode);
	}

	isConjunction(phi) {
		// Check if phi is a conjunction (P ^ Q)
		return phi instanceof TreeNode && phi.type === LogicalSymbols.TYPE_AND
	}

	isDisjunction(phi) {
		// Check if phi is a disjunction (P v Q)
		return phi instanceof TreeNode && phi.type === LogicalSymbols.TYPE_OR
	}

	flatten(arr) {
		return arr.reduce((acc, val) => acc.concat(val), []);
	}

	distributeAndOverOr(lists) {
		console.log("NormalForm.convertToDNFInternal: distributeAndOverOr: lists = ", lists)
		if (lists.length === 0) {
			return [[]]; // Empty list
		}

		let result = [];
		let rest = this.distributeAndOverOr(lists.slice(1)); // Recursive call for the remaining lists

		for (let value of lists[0]) {
			console.log("NormalForm.convertToDNFInternal: distributeAndOverOr: value = ", value)
			if(!Array.isArray(value)){
				value = [value]
			}
			if(rest.length === 0){
				result.push([...value])
			} else {
				for (const r of rest) {
					result.push([...value, ...this.flatten(r)]);
				}
			}
		}
		console.log("NormalForm.convertToDNFInternal: distributeAndOverOr: result = ", result)
		console.log("NormalForm.convertToDNFInternal: distributeAndOverOr: rest = ", rest)


		return result;
	}




	arrayToTreeNodes(array){
		let res = []
		for(let i = 0; i < array.length; i++) {
			let element = array[i];
			if(element === LogicalSymbols.AND_START || element === LogicalSymbols.OR_START){
				let treeNode = new TreeNode()
				res.push(treeNode)
				let subArray = this.findOperand(array, i)
				treeNode.type = element === LogicalSymbols.AND_START ? LogicalSymbols.TYPE_AND : LogicalSymbols.TYPE_OR;
				treeNode.operands.push(...this.arrayToTreeNodes(subArray))
				i += subArray.length + 1
			} else {
				res.push(element)
			}
		}
		return res
	}

	findOperand(array, startIndex){
		let element = array[startIndex];
		let j
			let closingSymbol = element === LogicalSymbols.AND_START ? LogicalSymbols.AND_END : LogicalSymbols.OR_END;
			let level = 0
			j = startIndex + 1;
			while(level !== 0 || array[j] !== closingSymbol) {
				if(array[j] === element) {
					level++;
				} else if(array[j] === closingSymbol) {
					level--;
				}
				j++;
			}
		return array.slice(startIndex + 1, j)
	}




}
